import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft, MapPin, User, Mail, Phone } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
}

const initialForm: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  postalCode: '',
  country: 'France',
};

export function CheckoutPage() {
  const navigate = useNavigate();
  const { items, totalPrice, clearCart } = useCart();
  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [success, setSuccess] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState<SupportedPhoneCountry>('FR');
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [phoneE164, setPhoneE164] = useState<string | null>(null);
  const [phoneIsValid, setPhoneIsValid] = useState(false);

  const handleChange = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  const validate = (): boolean => {
    const e: Partial<FormData> = {};
    if (!form.firstName.trim()) e.firstName = 'Requis';
    if (!form.lastName.trim()) e.lastName = 'Requis';
    if (!form.email.trim()) e.email = 'Requis';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email invalide';
    if (!phoneDisplay.trim()) e.phone = 'Requis';
    else if (!phoneIsValid || !phoneE164) e.phone = 'Numéro de téléphone invalide';
    if (!form.address.trim()) e.address = 'Requis';
    if (!form.city.trim()) e.city = 'Requis';
    if (!form.postalCode.trim()) e.postalCode = 'Requis';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !phoneE164) return;
    setForm((prev) => ({ ...prev, phone: phoneE164 }));
    clearCart();
    setSuccess(true);
  };

  if (items.length === 0 && !success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-textSecondary mb-4">Votre panier est vide.</p>
          <button onClick={() => navigate('/boutique')} className="btn-primary">
            Voir la boutique
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-2xl mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-display font-bold text-textPrimary mb-3">
            Commande confirmée !
          </h2>
          <p className="text-textSecondary mb-8">
            Merci pour votre commande, {form.firstName}. Vous recevrez une confirmation à {form.email}.
          </p>
          <button onClick={() => navigate('/boutique')} className="btn-primary">
            Retour à la boutique
          </button>
        </div>
      </div>
    );
  }

  const inputClass = (field: keyof FormData) =>
    `w-full px-4 py-3 rounded-lg border transition-colors ${
      errors[field]
        ? 'border-red-400 bg-red-50 focus:border-red-500'
        : 'border-gray-200 bg-white focus:border-primary-300'
    } outline-none text-textPrimary`;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/panier')}
            className="flex items-center gap-2 text-textSecondary hover:text-primary-500 transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Retour au panier
          </button>
          <h1 className="text-2xl font-display font-bold text-textPrimary">Finaliser la commande</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
            {/* Customer info */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center">
                  <User className="w-4 h-4 text-primary-400" />
                </div>
                <h2 className="font-semibold text-textPrimary">Informations personnelles</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textSecondary mb-1">Prénom</label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    className={inputClass('firstName')}
                    placeholder="Jean"
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-textSecondary mb-1">Nom</label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    className={inputClass('lastName')}
                    placeholder="Dupont"
                  />
                  {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-textSecondary mb-1">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> Email</span>
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={inputClass('email')}
                    placeholder="jean@email.com"
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-textSecondary mb-1">
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> Téléphone</span>
                  </label>
                  <InternationalPhoneInput
                    value={phoneDisplay}
                    country={phoneCountry}
                    required
                    onChange={(result) => {
                      setPhoneDisplay(result.displayValue);
                      setPhoneCountry(result.country);
                      setPhoneE164(result.e164Value);
                      setPhoneIsValid(result.isValid);
                      setForm((prev) => ({ ...prev, phone: result.displayValue }));
                      if (result.isValid && errors.phone) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.phone;
                          return next;
                        });
                      }
                    }}
                    selectClassName="w-full mb-2 px-4 py-3 rounded-lg border border-gray-200 bg-white"
                    inputClassName={inputClass('phone')}
                    placeholder="+33 6 00 00 00 00"
                    error={errors.phone}
                  />
                </div>
              </div>
            </div>

            {/* Shipping address */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-primary-400" />
                </div>
                <h2 className="font-semibold text-textPrimary">Adresse de livraison</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-textSecondary mb-1">Adresse</label>
                  <input
                    type="text"
                    value={form.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    className={inputClass('address')}
                    placeholder="12 rue de la Paix"
                  />
                  {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-textSecondary mb-1">Code postal</label>
                    <input
                      type="text"
                      value={form.postalCode}
                      onChange={(e) => handleChange('postalCode', e.target.value)}
                      className={inputClass('postalCode')}
                      placeholder="75001"
                    />
                    {errors.postalCode && <p className="text-red-500 text-xs mt-1">{errors.postalCode}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-textSecondary mb-1">Ville</label>
                    <input
                      type="text"
                      value={form.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      className={inputClass('city')}
                      placeholder="Paris"
                    />
                    {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-textSecondary mb-1">Pays</label>
                  <input
                    type="text"
                    value={form.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    className={inputClass('country')}
                  />
                </div>
              </div>
            </div>

            <button type="submit" className="btn-primary w-full text-base flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Valider la commande
            </button>
          </form>

          {/* Order summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
              <h2 className="font-semibold text-textPrimary mb-4">Résumé de la commande</h2>
              <div className="space-y-3 mb-4">
                {items.map(({ product, quantity }) => (
                  <div key={product.id} className="flex items-center gap-3">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-textPrimary truncate">{product.name}</p>
                      <p className="text-xs text-textSecondary">× {quantity}</p>
                    </div>
                    <span className="text-sm font-semibold text-textPrimary flex-shrink-0">
                      {(product.price * quantity).toFixed(2)} €
                    </span>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between text-sm text-textSecondary mb-2">
                  <span>Sous-total</span>
                  <span>{totalPrice.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm text-textSecondary mb-4">
                  <span>Livraison</span>
                  <span className="text-green-600 font-medium">Gratuite</span>
                </div>
                <div className="flex justify-between font-bold text-textPrimary text-lg">
                  <span>Total</span>
                  <span className="text-primary-400">{totalPrice.toFixed(2)} €</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
